import './src/styles/global.css';
import './src/styles/naranja-galpon.css';
import * as React from 'react';
import { ThemeProvider } from '@mui/material/styles';
import theme from './src/theme';

export const wrapRootElement = ({ element }) => (
  <ThemeProvider theme={theme}>{element}</ThemeProvider>
);

/* Las páginas del rediseño (html.ng-page) combinan scroll-behavior: smooth con
   scroll-snap-type: y mandatory, y eso rompe el scroll-to-top de Gatsby al
   navegar desde el fondo de una página: la nueva queda mostrando el footer.
   Dos mecanismos distintos lo causan:

   1. Con snap mandatory activo, todo scroll programático se convierte en
      animación (incluso con behavior auto) y cualquier relayout posterior
      (imágenes que cargan) la cancela y re-asienta el scroll en el "snap
      target recordado" — el footer, capturado cuando el DOM nuevo se clampeó
      al fondo. Por eso se apaga el snap ANTES de que el DOM nuevo haga layout
      (onPreRouteUpdate) y se re-enciende recién con la primera interacción
      del usuario: re-encenderlo a los pocos frames vuelve a disparar la
      re-resolución, con timing distinto según navegador y viewport.

   2. Aun sin snap, el clamp del compositor (la página nueva es más corta que
      la vieja) puede pisar el scrollTo(0,0) hecho en componentDidUpdate, que
      corre antes del layout del DOM nuevo. Por eso, tras el salto, un guard
      re-fija el destino durante un rato — chequeando por evento scroll y por
      intervalo (leer scrollY además fuerza la sincronización con el
      compositor) — y se desarma con cualquier interacción del usuario. */

const html = () => document.documentElement;

/* behavior:'instant' explícito: pisa el smooth del CSS de forma sincrónica.
   El truco de setear scrollBehavior:'auto' inline y restaurarlo en el mismo
   tick NO alcanza — Firefox evalúa el estilo perezosamente, ve 'smooth' y
   anima igual. Fallback por si algún browser viejo rechaza el enum. */
const instantScrollTo = (y) => {
  try {
    window.scrollTo({ top: y, left: 0, behavior: 'instant' });
  } catch (e) {
    html().style.scrollBehavior = 'auto';
    window.scrollTo(0, y);
    html().style.scrollBehavior = '';
  }
};

let snapRestoreArmed = false;

export const onPreRouteUpdate = () => {
  html().style.scrollSnapType = 'none';
  if (snapRestoreArmed) return;
  snapRestoreArmed = true;
  const restore = () => {
    window.removeEventListener('wheel', restore);
    window.removeEventListener('touchstart', restore);
    window.removeEventListener('keydown', restore);
    window.removeEventListener('pointerdown', restore);
    snapRestoreArmed = false;
    html().style.scrollSnapType = '';
  };
  window.addEventListener('wheel', restore, { passive: true });
  window.addEventListener('touchstart', restore, { passive: true });
  window.addEventListener('keydown', restore);
  window.addEventListener('pointerdown', restore);
};

let cancelActiveGuard = null;

const pinScrollTo = (targetY) => {
  if (cancelActiveGuard) cancelActiveGuard();
  instantScrollTo(targetY());
  const check = () => {
    if (Math.abs(window.scrollY - targetY()) > 2) instantScrollTo(targetY());
  };
  const stop = () => {
    cancelActiveGuard = null;
    window.clearInterval(intervalId);
    window.clearTimeout(timeoutId);
    window.removeEventListener('wheel', stop);
    window.removeEventListener('touchstart', stop);
    window.removeEventListener('keydown', stop);
    window.removeEventListener('pointerdown', stop);
    window.removeEventListener('scroll', check);
  };
  const intervalId = window.setInterval(check, 100);
  const timeoutId = window.setTimeout(stop, 2000);
  cancelActiveGuard = stop;
  window.addEventListener('wheel', stop, { passive: true });
  window.addEventListener('touchstart', stop, { passive: true });
  window.addEventListener('keydown', stop);
  window.addEventListener('pointerdown', stop);
  window.addEventListener('scroll', check, { passive: true });
};

export const shouldUpdateScroll = ({
  prevRouterProps,
  routerProps: { location },
  getSavedScrollPosition,
}) => {
  /* Ancla dentro de la misma página (click en <a href="#...">): el navegador
     ya scrollea nativo con el smooth+snap del CSS — no pisar esa animación. */
  if (
    location.hash &&
    prevRouterProps &&
    prevRouterProps.location.pathname === location.pathname
  ) {
    return false;
  }
  const anchorId = location.hash ? decodeURI(location.hash).substring(1) : null;
  const savedY = getSavedScrollPosition(location)[1] || 0;
  pinScrollTo(() => {
    const anchor = anchorId ? document.getElementById(anchorId) : null;
    return anchor
      ? anchor.getBoundingClientRect().top + window.scrollY
      : savedY;
  });
  return false;
};
